import { useCallback } from 'react';

import PropTypes from 'prop-types';

import Box from '@zenbusiness/zui/Box';
import Stack from '@zenbusiness/zui/Stack';

import * as Styled from './ProductCard.style';
import { CLICKSTREAM_COMMON_PROPS } from '../utils/constants';
import { highlightText } from '../utils/highlightText';

/**
 * A component for displaying product cards with consistent styling
 * @param {object} props - Component props
 * @param {string} [props.title] - The title of the product card
 * @param {string} [props.subtitle] - The description of the product card
 * @param {string} [props.imageUrl] - The URL of the image of the product card
 * @param {string} [props.ctaText] - The text of the CTA button
 * @param {string} [props.ctaUrl] - The URL of the CTA button
 * @param {string} [props.icon] - The URL of the icon of the product card
 * @param {Function} [props.onClick] - Optional callback when card is clicked
 * @param {Function} [props.onComponentClick] - Optional callback for overlay integration
 * @returns {import('react').ReactElement|null} The product card component or null if no content
 */
function ProductCard({
  title,
  subtitle,
  imageUrl,
  ctaText,
  ctaUrl,
  icon,
  onClick,
  onComponentClick
}) {
  const hasContent = !!title || !!subtitle || !!imageUrl || !!icon;

  const handleClick = useCallback(
    (event) => {
      event.preventDefault();

      if (onComponentClick) {
        onComponentClick({
          type: 'product',
          payload: {
            title,
            subtitle,
            imageUrl,
            icon,
            ctaUrl,
            ctaText
          }
        });
      }

      if (onClick) {
        onClick({ title, subtitle, ctaUrl });
      }
    },
    [title, subtitle, imageUrl, icon, ctaUrl, ctaText, onClick, onComponentClick]
  );

  if (!hasContent) {
    return null;
  }

  return (
    <Styled.ProductCard>
      <Box>
        <Styled.ProductCardImage>
          {imageUrl && <Styled.Image alt={title || 'Product image'} src={imageUrl} />}
          {icon && <Styled.ProductCardIcon alt={`${title} icon`} src={icon} />}
        </Styled.ProductCardImage>
        <Styled.ProductCardContent>
          <Stack gap={0.5}>
            {title && <Styled.ProductCardTitle size="xs">{title}</Styled.ProductCardTitle>}
            {subtitle && (
              <Styled.ProductCardDescription fontWeight="600" size="md">
                {subtitle}
              </Styled.ProductCardDescription>
            )}
          </Stack>
          {ctaText && (
            <Styled.ProductCardButton
              clickstreamProps={{
                ...CLICKSTREAM_COMMON_PROPS,
                elementName: 'velo_product_upsell',
                elementText: ctaText
              }}
              color="primary"
              fullWidth
              size="medium"
              variant="contained"
              onClick={handleClick}
            >
              {highlightText(ctaText, Styled.TextHighlight)}
              <Styled.ArrowForwardIcon />
            </Styled.ProductCardButton>
          )}
        </Styled.ProductCardContent>
      </Box>
    </Styled.ProductCard>
  );
}

ProductCard.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  imageUrl: PropTypes.string.isRequired,
  ctaText: PropTypes.string.isRequired,
  ctaUrl: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  onComponentClick: PropTypes.func
};

export default ProductCard;
